// NOKE 專櫃管理 — warehouse-side admin for the 忠泰樂生活 pop-up counter.
// Tabs: 進貨/標籤 (register pieces + print barcode price tags — Metanoia only;
// MdoC carries its own packaging barcode) · 中文標籤 (Taiwan compliance stickers
// for imported cosmetics, 45x60mm / 50x80mm) · 庫存 (on-hand inventory + adjust
// + reprint) · 銷售紀錄 (sales list + reverse) · 日結 (daily close-out numbers
// checked against the mall POS Z-report).
//
// All backend calls hit /api/noke/* through the shared axios wrapper (admin JWT
// auto-injected). There is NO payment handling here — the mall POS takes payment
// and issues the 發票; this UI only registers stock, builds/records sales for the
// vendor split, and prints tags.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Autocomplete,
  Button,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  FormControlLabel,
  Switch,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PrintIcon from '@mui/icons-material/Print';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import RestockTab from '../components/noke/RestockTab';
import RestockV2Tab from '../components/noke/restock-v2/RestockV2Tab';
import NokeInventoryTransfer, { NokeStockLocation } from '../components/NokeInventoryTransfer';
import ScanShelveDialog from '../components/noke/ScanShelveDialog';
import ReconcileScanDialog from '../components/noke/ReconcileScanDialog';
import { NOKE_SLOTS, normalizeSlot, printShelfLabels, slotSortKey } from '../utils/nokeSlots';
import { pdpUrl } from '../utils/nokePdp';
import {
  printNokeLabels,
  NokeLabelData,
  printCalibrationLabel,
  readLabelOffset,
  OFFSET_X_KEY,
  OFFSET_Y_KEY,
} from '../utils/nokeLabelPrint';
import {
  CnLabelData,
  CnLabelSize,
  CN_LABEL_SIZES,
  DEFAULT_CN_LABEL_SIZE,
  CN_OFFSET_X_KEY,
  CN_OFFSET_Y_KEY,
  readCnLabelOffset,
  printNokeCnLabels,
  previewNokeCnLabels,
  printCnCalibrationLabel,
  groupCnLabelsBySize,
  isCnLabelSize,
  CnSheetMode,
  CN_SHEET_MODES,
  CN_SHEET_KEY,
  DEFAULT_CN_SHEET_MODE,
  readCnSheetMode,
  cnSheetLayout,
} from '../utils/nokeCnLabelPrint';
import { vendorBucket, bucketVendorTotals } from '../utils/nokeVendor';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { useAuth } from '../contexts/AuthContext';
import { isCounterRole } from '../config/permissions';

// ---- Types (mirror the /api/noke contract verbatim) -----------------------

interface NokeVariant {
  variant_id: number;
  product_id: number;
  product_title: string;
  vendor: string;
  sku: string;
  color: string;
  size: string;
  price: number;
  image_url: string | null;
  /** '中國' | 'Made in Korea'; may be absent on older data. */
  origin?: string | null;
  /** storefront_products.status — 'archived' = 已下架 on metanoia.tw. */
  status?: string | null;
}

interface StockInResultItem {
  variant_id: number;
  sku: string;
  product_title: string;
  vendor: string;
  color: string;
  size: string;
  price: number;
  qty: number;
  barcode: string;
  /** '中國' | 'Made in Korea'; may be absent on older data. */
  origin?: string | null;
  /** Manufacturer barcode (e.g. MdoC EAN) when registered instead of NK code. */
  custom_barcode?: string | null;
  /** True for off-catalog items registered via 自訂商品進貨. */
  is_custom?: boolean;
  /** Uploaded photo URL (custom items only). */
  image_url?: string | null;
  /** 'staff' | 'cn_label' | 'existing'; null = no scannable code registered. */
  barcode_source?: string | null;
}

interface InventoryRow {
  variant_id: number;
  sku: string;
  product_title: string;
  vendor: string;
  color: string;
  size: string;
  price: number;
  qty_on_hand: number;
  qty_office?: number;
  qty_in_transit?: number;
  barcode: string;
  updated_at: string;
  /** '中國' | 'Made in Korea'; may be absent on older data. */
  origin?: string | null;
  /** Units sold in the last 7 / 30 days — restock velocity signals. */
  sold_7d?: number;
  sold_30d?: number;
  image_url?: string | null;
  /** Manufacturer barcode (e.g. MdoC EAN); when set it's the real scannable code. */
  custom_barcode?: string | null;
  /** True for off-catalog items registered via 自訂商品進貨. */
  is_custom?: boolean;
  /** Receiving-page 標籤 (Korean source store) for this SKU; null if none. */
  source_tag?: string | null;
  /** 櫃位 of the backstock in the wall cabinet ('A3'); null = unrecorded. */
  storage_slot?: string | null;
  /** Is a piece of this variant out on the counter shelf right now? */
  on_display?: boolean;
  /**
   * storefront_products.handle for the linked catalog product; null/absent for
   * custom items and for rows served by endpoints that don't join it. When set,
   * the 品名 cell links to the metanoia.tw PDP.
   */
  handle?: string | null;
}

interface GalleryImage {
  src: string;
  position: number;
}

interface SaleItem {
  variant_id: number;
  sku?: string;
  product_title?: string;
  vendor?: string;
  color?: string;
  size?: string;
  qty: number;
  unit_price: number;
  image_url?: string | null;
}

interface Sale {
  id: number;
  sale_ref: string;
  gross: number;
  discount: number;
  net_total: number;
  vendor_totals: Record<string, number>;
  customer_id: number | null;
  customer_name: string | null;
  sold_at: string;
  items: SaleItem[];
  salesperson?: string | null;
  // Reversal is inferred defensively — see isReversed().
  status?: string;
  reversed_at?: string | null;
}

interface DailyStats {
  date: string;
  tx_count: number;
  items_sold: number;
  gross: number;
  discount: number;
  net_total: number;
  vendor_totals: Record<string, number>;
  /** Transactions that included ≥1 MdoC item, and the attach rate (0–1). */
  mdoc_tx_count?: number;
  mdoc_attach_rate?: number;
}

// ---- Shared helpers -------------------------------------------------------

// Vendor display uses the shared vendorBucket()/bucketVendorTotals() rule
// (src/utils/nokeVendor.ts) so it matches the backend's Metanoia/MdoC split
// exactly — never a label map over raw vendor keys.

const formatNT = (amount: number | string | null | undefined): string => {
  const n = typeof amount === 'number' ? amount : parseFloat(String(amount ?? 0));
  if (isNaN(n)) return 'NT$ 0';
  return `NT$ ${Math.round(n).toLocaleString('en-US')}`;
};

const todayStr = (): string => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const formatDateTime = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('zh-TW', { hour12: false });
};

// The effective scannable barcode for a row/item: the manufacturer barcode when
// registered, else the system NK code. Used for both display and label encoding.
const effectiveBarcode = (r: { custom_barcode?: string | null; barcode?: string | null }): string =>
  (r.custom_barcode && r.custom_barcode.trim()) || r.barcode || '';

// MdoC pieces get NO NOKE hang-tag. The packaging already carries the brand's
// own scannable barcode (registered here as 原廠條碼, which is what the POS
// looks up), and the label that legally must be ON the product is the 中文標籤
// compliance sticker — printed from its own tab. A second tag would put two
// barcodes on one item and invite the wrong scan at the counter.
const wearsHangTag = (r: { vendor?: string | null }): boolean => vendorBucket(r.vendor) !== 'MdoC';

// Small square product thumbnail with a graceful placeholder when src is null.
const Thumb: React.FC<{ src?: string | null; alt?: string; size?: number; onClick?: () => void }> = ({
  src,
  alt,
  size = 44,
  onClick,
}) => (
  <Box
    onClick={onClick}
    title={onClick ? '查看商品圖片' : undefined}
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: 1,
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      bgcolor: 'grey.100',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid #e0e0e0',
    }}
  >
    {src ? (
      <img src={src} alt={alt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    ) : (
      <ImageOutlinedIcon fontSize="small" sx={{ color: 'grey.400' }} />
    )}
  </Box>
);

const clampOffset = (n: number): number => Math.min(10, Math.max(-10, Math.round(n * 2) / 2));

// 列印校正 — self-contained button + dialog to nudge the label print position.
// Offsets persist in localStorage and apply to every subsequent label print.
const CalibrationButton: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);

  // Load the saved offset each time the dialog opens.
  const handleOpen = () => {
    const { offsetX, offsetY } = readLabelOffset();
    setX(offsetX);
    setY(offsetY);
    setOpen(true);
  };

  const persist = (nx: number, ny: number) => {
    localStorage.setItem(OFFSET_X_KEY, String(nx));
    localStorage.setItem(OFFSET_Y_KEY, String(ny));
  };

  const save = () => {
    persist(x, y);
    enqueueSnackbar('列印校正已儲存', { variant: 'success' });
    setOpen(false);
  };

  const reset = () => {
    setX(0);
    setY(0);
    persist(0, 0);
    enqueueSnackbar('列印校正已歸零', { variant: 'info' });
  };

  const field = (
    label: string,
    value: number,
    setValue: (n: number) => void
  ) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <IconButton size="small" onClick={() => setValue(clampOffset(value - 0.5))}>
        <RemoveIcon fontSize="small" />
      </IconButton>
      <TextField
        label={label}
        type="number"
        size="small"
        value={value}
        onChange={(e) => setValue(clampOffset(parseFloat(e.target.value) || 0))}
        inputProps={{ step: 0.5, min: -10, max: 10, style: { textAlign: 'center' } }}
      />
      <IconButton size="small" onClick={() => setValue(clampOffset(value + 0.5))}>
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <>
      <Button size="small" startIcon={<TuneIcon />} onClick={handleOpen}>
        列印校正
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>列印校正</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            印出來偏左 → X 調大；偏上 → Y 調大。單位為公釐 (mm)。
          </DialogContentText>
          {field('X 位移 (mm)', x, setX)}
          {field('Y 位移 (mm)', y, setY)}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => printCalibrationLabel(x, y)}
            sx={{ mt: 1 }}
          >
            測試列印
          </Button>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button color="inherit" onClick={reset}>
            歸零
          </Button>
          <Box>
            <Button onClick={() => setOpen(false)} sx={{ mr: 1 }}>
              取消
            </Button>
            <Button variant="contained" onClick={save}>
              儲存
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

// A sale counts as cancelled if the backend flags a reversal in any of the shapes
// it might use (status string or reversed_at timestamp).
const isReversed = (sale: Sale): boolean =>
  Boolean(sale.reversed_at) ||
  (typeof sale.status === 'string' &&
    ['reversed', 'cancelled', 'canceled', 'void', 'voided'].includes(sale.status.toLowerCase()));

// Render a vendor split (Metanoia / MdoC) from a vendor_totals map, re-bucketed
// through the shared backend-identical rule. Both buckets always show (even at
// NT$ 0) because 日結 reconciliation reads them against the mall POS Z-report.
const VendorSplit: React.FC<{ totals: Record<string, number> | undefined; dense?: boolean }> = ({
  totals,
  dense,
}) => {
  const buckets = bucketVendorTotals(totals);
  return (
    <Box sx={{ display: 'flex', gap: dense ? 1.5 : 3, flexWrap: 'wrap' }}>
      {Object.entries(buckets).map(([bucket, amount]) => (
        <Typography key={bucket} variant={dense ? 'body2' : 'subtitle1'} color="text.secondary">
          {bucket}: <strong>{formatNT(amount)}</strong>
        </Typography>
      ))}
    </Box>
  );
};

// =============================================================================
// Tab 1 — 進貨 / 標籤
// =============================================================================

// One product (many variants) grouped for the per-product stock-in card.
interface ProductGroup {
  key: string;
  product_title: string;
  sku: string;
  vendor: string;
  image_url: string | null;
  colors: string[]; // distinct non-empty option1, first-seen order
  sizes: string[]; // distinct non-empty option2, first-seen order
  variants: NokeVariant[];
  archived: boolean; // 下架 on the storefront; only present when 顯示已下架 is on
}

// Group variant-search rows by product (product_id, falling back to sku), so a
// knit in 6 colours × 3 sizes renders ONE card with dropdowns instead of ~18
// near-identical cards. First-seen product order is preserved.
const groupVariantsByProduct = (variants: NokeVariant[]): ProductGroup[] => {
  const order: string[] = [];
  const map: Record<string, ProductGroup> = {};
  variants.forEach((v) => {
    const key = v.product_id != null ? `p${v.product_id}` : `s${v.sku}`;
    if (!map[key]) {
      map[key] = {
        key,
        product_title: v.product_title,
        sku: v.sku,
        vendor: v.vendor,
        image_url: v.image_url,
        colors: [],
        sizes: [],
        variants: [],
        archived: v.status === 'archived',
      };
      order.push(key);
    }
    const g = map[key];
    g.variants.push(v);
    const color = (v.color || '').trim();
    const size = (v.size || '').trim();
    if (color && !g.colors.includes(color)) g.colors.push(color);
    if (size && !g.sizes.includes(size)) g.sizes.push(size);
  });
  return order.map((k) => map[k]);
};

const variantChipLabel = (v: NokeVariant): string =>
  [(v.color || '').trim(), (v.size || '').trim()].filter(Boolean).join('/') || v.sku;

interface ProductStockInCardProps {
  group: ProductGroup;
  qtyByVariant: Record<number, number>;
  barcodeByVariant: Record<number, string>;
  setQty: (variantId: number, qty: number) => void;
  setBarcode: (variantId: number, value: string) => void;
}

// Per-product stock-in card: colour/size dropdowns resolve to a single variant,
// whose qty stepper + 原廠條碼 field bind to the SHARED qtyByVariant /
// barcodeByVariant maps (so the existing batch model is untouched). All variants
// with qty>0 in this group are shown as removable summary chips.
const ProductStockInCard: React.FC<ProductStockInCardProps> = ({
  group,
  qtyByVariant,
  barcodeByVariant,
  setQty,
  setBarcode,
}) => {
  const hasColor = group.colors.length > 0;
  const hasSize = group.sizes.length > 0;

  // Pre-select a dimension that has exactly one value (nothing to choose).
  const [selectedColor, setSelectedColor] = useState(group.colors.length === 1 ? group.colors[0] : '');
  const [selectedSize, setSelectedSize] = useState(group.sizes.length === 1 ? group.sizes[0] : '');

  // Resolve (color,size) → variant. No dimensions => the lone variant. Takes the
  // first match if two variants somehow share the same (color,size).
  const resolvedVariant = useMemo(() => {
    if (!hasColor && !hasSize) return group.variants[0];
    if (hasColor && !selectedColor) return undefined;
    if (hasSize && !selectedSize) return undefined;
    return group.variants.find(
      (v) =>
        (!hasColor || (v.color || '').trim() === selectedColor) &&
        (!hasSize || (v.size || '').trim() === selectedSize)
    );
  }, [group, hasColor, hasSize, selectedColor, selectedSize]);

  const resolvedId = resolvedVariant?.variant_id;
  const qty = resolvedId != null ? qtyByVariant[resolvedId] || 0 : 0;

  // Highlight the card when ANY variant in the group has qty>0, not just the
  // one currently shown in the dropdown.
  const groupActive = group.variants.some((v) => (qtyByVariant[v.variant_id] || 0) > 0);

  const prices = group.variants.map((v) => v.price);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const priceText = resolvedVariant
    ? formatNT(resolvedVariant.price)
    : minP === maxP
      ? formatNT(minP)
      : `${formatNT(minP)} – ${formatNT(maxP)}`;

  const selectedInGroup = group.variants
    .map((v) => ({ v, q: qtyByVariant[v.variant_id] || 0 }))
    .filter((x) => x.q > 0);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: groupActive ? '2px solid #4c5e8a' : undefined,
      }}
    >
      {group.image_url && (
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <img
            src={group.image_url}
            alt={group.product_title}
            style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain' }}
          />
        </Box>
      )}
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {group.product_title}
      </Typography>
      <Box sx={{ mb: 1 }}>
        <Chip size="small" label={vendorBucket(group.vendor)} sx={{ mr: 0.5 }} />
        {group.archived && (
          <Chip size="small" color="warning" label="已下架" sx={{ mr: 0.5 }} />
        )}
        <Typography variant="body2" component="span" color="text.secondary">
          SKU: {group.sku}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        {priceText}
      </Typography>

      {/* Colour: dropdown when >1, static when exactly 1, absent when none. */}
      {hasColor &&
        (group.colors.length > 1 ? (
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel>顏色</InputLabel>
            <Select label="顏色" value={selectedColor} onChange={(e) => setSelectedColor(e.target.value)}>
              {group.colors.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" sx={{ mb: 1 }}>
            顏色：{group.colors[0]}
          </Typography>
        ))}

      {/* Size: dropdown when >1, static when exactly 1, absent when none. */}
      {hasSize &&
        (group.sizes.length > 1 ? (
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel>尺寸</InputLabel>
            <Select label="尺寸" value={selectedSize} onChange={(e) => setSelectedSize(e.target.value)}>
              {group.sizes.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" sx={{ mb: 1 }}>
            尺寸：{group.sizes[0]}
          </Typography>
        ))}

      {/* Qty stepper — bound to the resolved variant; disabled until a full
          valid combination is chosen. */}
      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          size="small"
          onClick={() => resolvedId != null && setQty(resolvedId, qty - 1)}
          disabled={resolvedId == null || qty <= 0}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <TextField
          size="small"
          type="number"
          value={qty}
          disabled={resolvedId == null}
          onChange={(e) => resolvedId != null && setQty(resolvedId, parseInt(e.target.value, 10) || 0)}
          inputProps={{ min: 0, style: { textAlign: 'center', width: 48 } }}
        />
        <IconButton
          size="small"
          onClick={() => resolvedId != null && setQty(resolvedId, qty + 1)}
          disabled={resolvedId == null}
        >
          <AddIcon fontSize="small" />
        </IconButton>
        {resolvedId == null && (
          <Typography variant="caption" color="text.secondary">
            請選擇{hasColor && !selectedColor ? '顏色' : ''}
            {hasColor && !selectedColor && hasSize && !selectedSize ? ' / ' : ''}
            {hasSize && !selectedSize ? '尺寸' : ''}
          </Typography>
        )}
      </Box>

      {/* Optional manufacturer barcode — a normal focusable input so the hardware
          scanner can type the packaging code into it. Edits the RESOLVED variant. */}
      <TextField
        size="small"
        label="原廠條碼 (選填)"
        placeholder="掃描包裝上的條碼"
        value={resolvedId != null ? barcodeByVariant[resolvedId] || '' : ''}
        disabled={resolvedId == null}
        onChange={(e) => resolvedId != null && setBarcode(resolvedId, e.target.value)}
        helperText="MdoC 等已有條碼的商品，掃描包裝上的條碼即可"
        sx={{ mt: 1 }}
        fullWidth
      />

      {/* Every variant in this group with qty>0 (visible across dropdown swaps). */}
      {selectedInGroup.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedInGroup.map(({ v, q }) => {
            const custom = (barcodeByVariant[v.variant_id] || '').trim();
            return (
              <Chip
                key={v.variant_id}
                size="small"
                color="primary"
                variant="outlined"
                label={`${variantChipLabel(v)} ×${q}${custom ? ' ∗' : ''}`}
                onDelete={() => setQty(v.variant_id, 0)}
              />
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

// Register an off-catalog custom item (e.g. a customized piece with no
// storefront listing). Posts multipart to /api/noke/custom-item — the backend
// mints a counter-only NK barcode and stores the uploaded photo on the row.
// The created item feeds the same label-print result panel as normal stock-in.
const CustomItemDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (item: StockInResultItem) => void;
}> = ({ open, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [qty, setQty] = useState('1');
  const [sku, setSku] = useState('');
  const [color, setColor] = useState('');
  const [size, setSize] = useState('');
  const [vendor, setVendor] = useState('Metanoia');
  const [origin, setOrigin] = useState('');
  const [customBarcode, setCustomBarcode] = useState('');
  const [location, setLocation] = useState<NokeStockLocation>('office');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setTitle(''); setPrice(''); setQty('1'); setSku(''); setColor('');
    setSize(''); setVendor('Metanoia'); setOrigin(''); setCustomBarcode('');
    setNote('');
    setLocation('office');
    setFile(null);
    setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
  };

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    // Allow re-picking the same file after clearing.
    e.target.value = '';
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      enqueueSnackbar('圖片過大（上限 8MB）', { variant: 'warning' });
      return;
    }
    setFile(f);
    setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
  };

  const submit = async () => {
    const p = Math.round(Number(price));
    const n = parseInt(qty, 10);
    if (!title.trim()) { enqueueSnackbar('請輸入品名', { variant: 'warning' }); return; }
    if (!Number.isFinite(p) || p <= 0) { enqueueSnackbar('請輸入有效價格', { variant: 'warning' }); return; }
    if (!Number.isFinite(n) || n <= 0) { enqueueSnackbar('請輸入有效數量', { variant: 'warning' }); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('product_title', title.trim());
      fd.append('price', String(p));
      fd.append('qty', String(n));
      fd.append('location', location);
      if (sku.trim()) fd.append('sku', sku.trim());
      if (color.trim()) fd.append('color', color.trim());
      if (size.trim()) fd.append('size', size.trim());
      fd.append('vendor', vendor);
      if (origin.trim()) fd.append('origin', origin.trim());
      if (customBarcode.trim()) fd.append('custom_barcode', customBarcode.trim());
      if (note.trim()) fd.append('note', note.trim());
      if (file) fd.append('image', file);
      const res = await api.post('/api/noke/custom-item', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const item: StockInResultItem = res.data.item;
      onCreated(item);
      enqueueSnackbar(`已建立自訂商品 ${item.sku}（${n} 件，${location === 'office' ? '辦公室' : 'NOKE 在櫃'}）`, { variant: 'success' });
      resetForm();
      onClose();
    } catch (err: any) {
      // 409 = 原廠條碼 already used; 503 = R2 not configured.
      enqueueSnackbar(err.response?.data?.message || '建立自訂商品失敗', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>自訂商品進貨</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          不在網店目錄的商品（例如客製化商品）。SKU 留空會自動產生（NKC-1、NKC-2⋯），
          條碼由系統配發，標籤照常列印。
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField select fullWidth size="small" label="登記位置" value={location}
              disabled={submitting} onChange={(e) => setLocation(e.target.value as NokeStockLocation)}
              helperText="辦公室登記可先列印標籤，送至專櫃並確認收貨後才計入在櫃數量。">
              <MenuItem value="office">辦公室／待送櫃</MenuItem>
              <MenuItem value="store">NOKE／已實際到櫃</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required size="small" label="品名" value={title}
              onChange={(e) => setTitle(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth required size="small" label="價格 (NT$)" value={price}
              onChange={(e) => setPrice(e.target.value)} inputProps={{ inputMode: 'numeric' }} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth required size="small" label="數量" value={qty}
              onChange={(e) => setQty(e.target.value)} inputProps={{ inputMode: 'numeric' }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>品牌</InputLabel>
              <Select label="品牌" value={vendor} onChange={(e) => setVendor(e.target.value)}>
                <MenuItem value="Metanoia">Metanoia</MenuItem>
                <MenuItem value="MdoC">MdoC</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label="SKU (選填)" value={sku}
              onChange={(e) => setSku(e.target.value)} placeholder="留空自動產生" />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label="顏色 (選填)" value={color}
              onChange={(e) => setColor(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label="尺寸 (選填)" value={size}
              onChange={(e) => setSize(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={6}>
            <TextField fullWidth size="small" label="產地 (選填，印在標籤)" value={origin}
              onChange={(e) => setOrigin(e.target.value)} placeholder="例：Made in Korea / 台灣" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="原廠條碼 (選填，可掃描輸入)" value={customBarcode}
              onChange={(e) => setCustomBarcode(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="備註 (選填)" value={note}
              onChange={(e) => setNote(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button variant="outlined" component="label" startIcon={<ImageOutlinedIcon />}>
                {file ? '更換照片' : '上傳商品照片 (選填)'}
                <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={pickFile} />
              </Button>
              {preview && (
                <Box sx={{ position: 'relative' }}>
                  <Box component="img" src={preview} alt="預覽"
                    sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                  <IconButton size="small" onClick={() => {
                    setFile(null);
                    setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
                  }}
                    sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'background.paper', boxShadow: 1 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>取消</Button>
        <Button variant="contained" onClick={submit} disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}>
          加入{location === 'office' ? '辦公室' : '在櫃'}庫存
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StockInTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  // 下架 products are excluded by default (the counter mostly receives current
  // stock); archivedTotal is how many the search matched but hid, so the panel
  // can say so instead of silently dropping them.
  const [includeArchived, setIncludeArchived] = useState(false);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [variants, setVariants] = useState<NokeVariant[]>([]);
  const [qtyByVariant, setQtyByVariant] = useState<Record<number, number>>({});
  // Optional per-variant manufacturer barcode (MdoC etc. — scan the packaging).
  const [barcodeByVariant, setBarcodeByVariant] = useState<Record<number, string>>({});
  const [note, setNote] = useState('');
  // 櫃位 for this whole batch — a 進貨 delivery is unpacked into the cabinet
  // together, and recording it here is the only moment staff are holding the
  // pieces. Optional: blank leaves the location unset (and a re-stock without
  // one preserves whatever the variant already had).
  const [batchSlot, setBatchSlot] = useState('');
  const [location, setLocation] = useState<NokeStockLocation>('office');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<StockInResultItem[] | null>(null);
  const [customOpen, setCustomOpen] = useState(false);

  // `withArchived` overrides the toggle so flipping it can re-run immediately,
  // without waiting for the state update to land.
  const handleSearch = async (withArchived: boolean = includeArchived) => {
    if (!q.trim()) {
      enqueueSnackbar('請輸入 SKU 或關鍵字搜尋', { variant: 'warning' });
      return;
    }
    setSearching(true);
    try {
      const res = await api.post('/api/noke/variant-search', {
        q: q.trim(),
        include_archived: withArchived,
      });
      const list: NokeVariant[] = res.data.variants || [];
      setVariants(list);
      setArchivedTotal(Number(res.data.archived_total) || 0);
      if (list.length === 0) enqueueSnackbar('找不到符合的商品變體', { variant: 'info' });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || '搜尋失敗', { variant: 'error' });
    } finally {
      setSearching(false);
    }
  };

  const toggleArchived = (next: boolean) => {
    setIncludeArchived(next);
    if (q.trim()) handleSearch(next);
  };

  const setQty = (variantId: number, qty: number) => {
    setQtyByVariant((prev) => ({ ...prev, [variantId]: Math.max(0, qty) }));
  };

  const setBarcode = (variantId: number, value: string) => {
    setBarcodeByVariant((prev) => ({ ...prev, [variantId]: value }));
  };

  // One card per product; dropdowns pick the variant within.
  const productGroups = useMemo(() => groupVariantsByProduct(variants), [variants]);

  const selectedItems = useMemo(
    () =>
      variants
        .map((v) => ({
          variant_id: v.variant_id,
          qty: qtyByVariant[v.variant_id] || 0,
          custom_barcode: (barcodeByVariant[v.variant_id] || '').trim() || undefined,
        }))
        .filter((it) => it.qty > 0),
    [variants, qtyByVariant, barcodeByVariant]
  );

  const handleStockIn = async () => {
    if (selectedItems.length === 0) {
      enqueueSnackbar('請為至少一個變體設定數量', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/api/noke/stock-in', {
        items: selectedItems,
        note: note.trim() || undefined,
        location,
        storage_slot: location === 'store' ? batchSlot || undefined : undefined,
      });
      const items: StockInResultItem[] = res.data.items || [];
      setResult(items);
      enqueueSnackbar(`已加入 ${items.reduce((s, i) => s + i.qty, 0)} 件至${location === 'office' ? '辦公室' : '在櫃'}庫存`, {
        variant: 'success',
      });
      // Reset the search selection; keep the result panel for label printing.
      setQtyByVariant({});
      setBarcodeByVariant({});
    } catch (err: any) {
      // 409 = the manufacturer barcode already belongs to another variant.
      enqueueSnackbar(err.response?.data?.message || '加入庫存失敗', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Rows this batch will actually produce hang-tags for (MdoC is excluded —
  // see wearsHangTag). Kept as a memo so the button count and the print call
  // can never disagree about what comes out of the printer.
  const taggableResult = useMemo(() => (result || []).filter(wearsHangTag), [result]);
  const skippedMdocPieces = useMemo(
    () => (result || []).filter((it) => !wearsHangTag(it)).reduce((s, i) => s + i.qty, 0),
    [result]
  );

  // MdoC rows that came out of stock-in with no registered EAN: no hang-tag and
  // no packaging code on file means nothing to scan at the till.
  const unscannableMdoc = useMemo(
    () => (result || []).filter((it) => !wearsHangTag(it) && !(it.custom_barcode || '').trim()).length,
    [result]
  );

  const printFromResult = () => {
    if (taggableResult.length === 0) return;
    const labels: NokeLabelData[] = taggableResult.map((it) => ({
      sku: it.sku,
      productTitle: it.product_title,
      color: it.color,
      size: it.size,
      price: it.price,
      // Manufacturer barcode when registered, else the NK system code.
      barcode: effectiveBarcode(it),
      origin: it.origin,
      copies: it.qty,
    }));
    printNokeLabels(labels);
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              fullWidth
              label="輸入 SKU / 關鍵字搜尋"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="輸入商品 SKU 或名稱"
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={searching || !q.trim()}
              startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            >
              搜尋
            </Button>
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Switch
                  checked={includeArchived}
                  onChange={(e) => toggleArchived(e.target.checked)}
                  disabled={searching}
                />
              }
              label="顯示已下架商品"
            />
          </Grid>
          <Grid item sx={{ ml: 'auto' }}>
            {/* Off-catalog items (e.g. customized pieces) skip the search. */}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCustomOpen(true)}>
              自訂商品進貨
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <CustomItemDialog
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        onCreated={(item) => setResult((prev) => [...(prev || []), item])}
      />

      {/* Never drop matches silently — say how many 下架 products are hidden. */}
      {!includeArchived && archivedTotal > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => toggleArchived(true)}>
              顯示
            </Button>
          }
        >
          另有 {archivedTotal} 件已下架商品符合此搜尋，未顯示。
        </Alert>
      )}

      {variants.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            選擇款式並設定進貨數量
          </Typography>
          <Grid container spacing={2}>
            {productGroups.map((group) => (
              <Grid item key={group.key} xs={12} sm={6} md={4}>
                <ProductStockInCard
                  group={group}
                  qtyByVariant={qtyByVariant}
                  barcodeByVariant={barcodeByVariant}
                  setQty={setQty}
                  setBarcode={setBarcode}
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField select size="small" label="登記位置" value={location} sx={{ minWidth: 220 }}
              disabled={submitting} onChange={(e) => setLocation(e.target.value as NokeStockLocation)}>
              <MenuItem value="office">辦公室／待送櫃</MenuItem>
              <MenuItem value="store">NOKE／已實際到櫃</MenuItem>
            </TextField>
            {location === 'store' && (
              <Autocomplete
                freeSolo
                size="small"
                options={NOKE_SLOTS}
                value={batchSlot}
                inputValue={batchSlot}
                onInputChange={(_, value) => setBatchSlot(normalizeSlot(value) || '')}
                onChange={(_, value) => setBatchSlot(normalizeSlot(value) || '')}
                disabled={submitting}
                sx={{ minWidth: 220 }}
                renderInput={(params) => (
                  <TextField {...params} label="放入櫃位 (選填)"
                    helperText="可選 A1–A10、B1–B10，或輸入其他櫃位"
                    inputProps={{ ...params.inputProps, maxLength: 16 }} />
                )}
              />
            )}
            <TextField
              label="備註 (選填)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              size="small"
              sx={{ minWidth: 240 }}
            />
            <Button
              variant="contained"
              onClick={handleStockIn}
              disabled={submitting || selectedItems.length === 0}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            >
              加入{location === 'office' ? '辦公室' : '在櫃'}庫存 ({selectedItems.reduce((s, it) => s + it.qty, 0)} 件)
            </Button>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            先在辦公室登記即可列印標籤。到「庫存」送往專櫃，再按實際收到件數確認收貨；請勿重複進貨。
          </Alert>
        </Paper>
      )}

      {result && result.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">進貨完成 — 可列印標籤</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalibrationButton />
              <Button
                variant="contained"
                color="primary"
                startIcon={<PrintIcon />}
                onClick={printFromResult}
                disabled={taggableResult.length === 0}
              >
                列印標籤 (共 {taggableResult.reduce((s, i) => s + i.qty, 0)} 張)
              </Button>
            </Box>
          </Box>
          {skippedMdocPieces > 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              MdoC {skippedMdocPieces} 件不印吊牌 — 包裝上已有原廠條碼，
              法規要求的<strong>中文標籤</strong>請到「中文標籤」分頁列印。
            </Alert>
          )}
          {unscannableMdoc > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              有 {unscannableMdoc} 項 MdoC 商品<strong>沒有可掃描的條碼</strong>
              （不印吊牌，且中文標籤未登記原廠條碼）— 這些商品在櫃檯結帳時掃不到。
              請到「中文標籤」分頁補上原廠條碼。
            </Alert>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>品名</TableCell>
                  <TableCell>顏色 / 尺寸</TableCell>
                  <TableCell align="right">價格</TableCell>
                  <TableCell align="right">數量</TableCell>
                  <TableCell>條碼</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.map((it) => (
                  <TableRow key={`${it.variant_id}-${it.barcode}`}>
                    <TableCell>{it.sku}</TableCell>
                    <TableCell>{it.product_title}</TableCell>
                    <TableCell>
                      {it.color}
                      {it.size ? ` / ${it.size}` : ''}
                    </TableCell>
                    <TableCell align="right">{formatNT(it.price)}</TableCell>
                    <TableCell align="right">{it.qty}</TableCell>
                    <TableCell>
                      {(() => {
                        const hasOwn = Boolean(it.custom_barcode && it.custom_barcode.trim());
                        // MdoC prints no hang-tag, so the NK code exists nowhere
                        // physical — without a registered 原廠條碼 the piece
                        // cannot be rung up at the counter at all.
                        if (!hasOwn && !wearsHangTag(it)) {
                          return (
                            <Tooltip title="MdoC 不印吊牌，未登記原廠條碼就無法在櫃檯掃描。請在「中文標籤」分頁補上原廠條碼後重新進貨，或手動掃描包裝條碼。">
                              <Chip size="small" color="error" label="無法掃描" sx={{ height: 18 }} />
                            </Tooltip>
                          );
                        }
                        return (
                          <>
                            <code>{effectiveBarcode(it)}</code>
                            {hasOwn && (
                              <Chip
                                size="small"
                                color={it.barcode_source === 'cn_label' ? 'success' : 'info'}
                                label={it.barcode_source === 'cn_label' ? '自動帶入' : '原廠'}
                                sx={{ ml: 0.5, height: 18 }}
                              />
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

// =============================================================================
// Tab 2 — 庫存
// =============================================================================

// Rows selling this many units in 7 days get a restock cue (bold + ▲ chip).
const VELOCITY_CUE_THRESHOLD = 3;

type InventorySort = 'default' | 'velocity';

const InventoryTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const isAdmin = !isCounterRole(currentUser?.role);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [sortMode, setSortMode] = useState<InventorySort>('default');
  const [highlightVariantId, setHighlightVariantId] = useState<number | null>(null);
  const [adjustRow, setAdjustRow] = useState<InventoryRow | null>(null);
  const [adjustDelta, setAdjustDelta] = useState('0');
  const [adjustLocation, setAdjustLocation] = useState<NokeStockLocation>('store');
  const [adjustNote, setAdjustNote] = useState('');
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);

  // Product-gallery dialog state (fetched lazily per variant on thumbnail tap).
  const [galleryRow, setGalleryRow] = useState<InventoryRow | null>(null);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState(false);

  // Custom (manufacturer) barcode edit dialog.
  const [barcodeRow, setBarcodeRow] = useState<InventoryRow | null>(null);
  const [barcodeValue, setBarcodeValue] = useState('');
  const [barcodeSubmitting, setBarcodeSubmitting] = useState(false);

  // Remove-from-inventory confirm dialog.
  const [deleteRow, setDeleteRow] = useState<InventoryRow | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // 掃碼上架／下架 — the put-away screen. Opened from the toolbar; it writes
  // through /inventory/scan and merges each updated row back into this table.
  const [scanOpen, setScanOpen] = useState(false);

  // 盤點模式 — rewrites 上架 or one 櫃位 to match what was scanned (moves no
  // quantity). lastReconciled = the 「上次重盤」 stamp per 'display' / 'slot:A1'.
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [lastReconciled, setLastReconciled] = useState<Record<string, string>>({});

  // 櫃位 / 上架狀態 edit dialog.
  const [slotRow, setSlotRow] = useState<InventoryRow | null>(null);
  const [slotValue, setSlotValue] = useState('');
  const [slotOnDisplay, setSlotOnDisplay] = useState(true);
  const [slotSubmitting, setSlotSubmitting] = useState(false);
  /** Pieces to carry 辦公室 → 在櫃 as part of this save; 0 = flag/slot only. */
  const [slotMoveIn, setSlotMoveIn] = useState(0);

  // Shelf filters + the 依櫃位 (put-away / 盤點) view. Both are client-side: the
  // tab already holds the whole inventory in `rows`, so filtering server-side
  // would only add a round trip and a second source of truth.
  const [filterMode, setFilterMode] = useState<'all' | 'office' | 'transit' | 'store' | 'offshelf' | 'noslot'>('store');
  const [viewMode, setViewMode] = useState<'list' | 'slots'>('list');

  const fetchInventory = useCallback(
    async (query: string, sort: InventorySort = 'default') => {
      setLoading(true);
      try {
        const res = await api.get('/api/noke/inventory', {
          params: {
            q: query || undefined,
            sort: sort === 'velocity' ? 'velocity' : undefined,
          },
        });
        setRows(res.data.items || []);
      } catch (err: any) {
        enqueueSnackbar(err.response?.data?.message || '載入庫存失敗', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchInventory('', 'default');
  }, [fetchInventory]);

  const fetchLastReconciled = useCallback(async () => {
    try {
      const res = await api.get('/api/noke/inventory/reconcile/last');
      const map: Record<string, string> = {};
      (res.data.sessions || []).forEach((s: { target: string; slot: string | null; created_at: string }) => {
        map[s.target === 'display' ? 'display' : `slot:${s.slot}`] = s.created_at;
      });
      setLastReconciled(map);
    } catch {
      /* stamp is informational — a failed read just hides it */
    }
  }, []);

  useEffect(() => {
    fetchLastReconciled();
  }, [fetchLastReconciled]);

  /** 「上次重盤：M/D」 (Asia/Taipei), or null when never fully reconciled. */
  const reconciledLabel = (key: string): string | null => {
    const at = lastReconciled[key];
    if (!at) return null;
    return `上次重盤：${new Date(at).toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric', timeZone: 'Asia/Taipei' })}`;
  };

  const toggleVelocitySort = () => {
    const next: InventorySort = sortMode === 'velocity' ? 'default' : 'velocity';
    setSortMode(next);
    fetchInventory(q, next);
  };

  const openGallery = async (row: InventoryRow) => {
    setGalleryRow(row);
    setGalleryImages([]);
    setGalleryError(false);
    setGalleryLoading(true);
    try {
      const res = await api.get(`/api/noke/inventory/${row.variant_id}/images`);
      setGalleryImages(res.data.images || []);
    } catch (err: any) {
      setGalleryError(true);
    } finally {
      setGalleryLoading(false);
    }
  };

  // Search-by-scan for stocktakes: a hardware-scanner burst while this tab is
  // open resolves the barcode via /lookup, filters the table to that SKU and
  // highlights the matching row. (The tab is only mounted while selected, so
  // the listener detaches automatically when leaving the tab.)
  const handleScan = useCallback(
    async (code: string) => {
      try {
        const res = await api.get(`/api/noke/lookup/${encodeURIComponent(code)}`);
        if (res.data.found && res.data.item) {
          const item = res.data.item as { variant_id: number; sku: string };
          setQ(item.sku);
          setHighlightVariantId(item.variant_id);
          await fetchInventory(item.sku, sortMode);
        } else {
          setHighlightVariantId(null);
          enqueueSnackbar(`找不到條碼 ${code}`, { variant: 'error' });
        }
      } catch (err: any) {
        enqueueSnackbar(err.response?.data?.message || '條碼查詢失敗', { variant: 'error' });
      }
    },
    [fetchInventory, enqueueSnackbar, sortMode]
  );

  // Detached while a scan dialog is open: those dialogs own the scanner, and a
  // burst reaching this lookup too would re-filter and refetch the table mid-scan.
  useBarcodeScanner({ onScan: handleScan, enabled: !scanOpen && !reconcileOpen });

  const openAdjust = (row: InventoryRow) => {
    setAdjustRow(row);
    setAdjustDelta('0');
    setAdjustLocation(row.qty_on_hand > 0 ? 'store' : 'office');
    setAdjustNote('');
  };

  const openBarcodeEdit = (row: InventoryRow) => {
    setBarcodeRow(row);
    setBarcodeValue(row.custom_barcode || '');
  };

  // Set or clear a manufacturer barcode. Empty value clears it (backend nulls it).
  const submitBarcode = async (clear = false) => {
    if (!barcodeRow) return;
    const value = clear ? '' : barcodeValue.trim();
    setBarcodeSubmitting(true);
    try {
      const res = await api.post('/api/noke/inventory/set-barcode', {
        variant_id: barcodeRow.variant_id,
        custom_barcode: value || null,
      });
      applyRowUpdate(res.data.item);
      enqueueSnackbar(value ? '原廠條碼已更新' : '原廠條碼已清除', { variant: 'success' });
      setBarcodeRow(null);
    } catch (err: any) {
      // 404 = not in inventory; 409 = barcode already used by another variant.
      enqueueSnackbar(err.response?.data?.message || '條碼設定失敗', { variant: 'error' });
    } finally {
      setBarcodeSubmitting(false);
    }
  };

  // Remove a variant from counter inventory (irreversible).
  const confirmDelete = async () => {
    if (!deleteRow) return;
    setDeleteSubmitting(true);
    try {
      await api.delete(`/api/noke/inventory/${deleteRow.variant_id}`);
      setRows((prev) => prev.filter((r) => r.variant_id !== deleteRow.variant_id));
      enqueueSnackbar('已移除', { variant: 'success' });
      setDeleteRow(null);
    } catch (err: any) {
      // 404 = not in inventory.
      enqueueSnackbar(err.response?.data?.message || '移除失敗', { variant: 'error' });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const submitAdjust = async () => {
    if (!adjustRow) return;
    const delta = Number(adjustDelta);
    if (!Number.isSafeInteger(delta) || !delta) {
      enqueueSnackbar('請輸入非零整數的調整數量 (可為負數)', { variant: 'warning' });
      return;
    }
    setAdjustSubmitting(true);
    try {
      const res = await api.post('/api/noke/inventory/adjust', {
        variant_id: adjustRow.variant_id,
        location: adjustLocation,
        delta,
        note: adjustNote.trim() || undefined,
      });
      applyRowUpdate(res.data.item);
      enqueueSnackbar('庫存已調整', { variant: 'success' });
      setAdjustRow(null);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || '調整失敗', { variant: 'error' });
    } finally {
      setAdjustSubmitting(false);
    }
  };

  // Patch a single row from a mutation response instead of refetching the list.
  // A refetch flips `loading`, swaps the whole table for a spinner and drops the
  // staffer's scroll position — for a one-field edit that reads as the page
  // reloading itself.
  //
  // MERGE, never replace: the mutation routes serialize a bare RETURNING * row,
  // so they carry no image_url / sold_7d / sold_30d / source_tag / handle (those
  // are joined on GET /inventory only, and the serializer omits the keys
  // entirely rather than sending undefined). Spreading the old row first keeps
  // the thumbnail and the sell-through numbers on screen.
  const applyRowUpdate = useCallback((item: InventoryRow) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.variant_id !== item.variant_id) return r;
        const merged = { ...r, ...item };
        // image_url is the one field the merge cannot take at face value: it IS
        // a real column (the uploaded photo on custom items), so a RETURNING *
        // row always carries the key — but GET /inventory SHADOWS it with a
        // COALESCE'd live catalog image, which for a catalog row means the
        // mutation hands back null where the list had a real URL. Taking it
        // would blank the thumbnail on every save. A custom item's mutation row
        // carries its actual URL, so it still updates.
        if (item.image_url == null) merged.image_url = r.image_url;
        return merged;
      })
    );
  }, []);

  const openSlotEdit = (row: InventoryRow) => {
    setSlotRow(row);
    setSlotValue(row.storage_slot || '');
    setSlotOnDisplay(row.on_display !== false);
    // 「已上架」 means 「我已經把貨帶到門市了」, so when the stock is still booked to
    // the office the dialog offers to carry that move — pre-filled with the whole
    // office quantity, which is what 「我把這批帶過來了」 means in practice.
    setSlotMoveIn(row.qty_on_hand > 0 ? 0 : (row.qty_office ?? 0));
  };

  // Writes 櫃位 and 上架狀態 together. Both are always sent so the dialog's two
  // controls can never disagree with what lands in the DB.
  const submitSlot = async () => {
    if (!slotRow) return;
    setSlotSubmitting(true);
    try {
      // The move only makes sense alongside 已上架 — 「收在櫃內」 does not imply the
      // pieces travelled, so an off-display save never silently relocates stock.
      const moveIn = slotOnDisplay ? slotMoveIn : 0;
      const res = await api.post('/api/noke/inventory/set-slot', {
        variant_id: slotRow.variant_id,
        storage_slot: slotValue || null,
        on_display: slotOnDisplay,
        ...(moveIn > 0 ? { move_from_office: moveIn } : {}),
      });
      const saved = res.data.item;
      applyRowUpdate(saved);
      // Name what landed, not that "something" landed: the two writes this
      // dialog makes are independent, and the shelf flag has no other visible
      // acknowledgement at the moment of saving.
      enqueueSnackbar(
        `${moveIn > 0 ? `已從辦公室移入 ${moveIn} 件 · ` : ''}${
          saved.on_display === false ? '未上架' : '已上架'
        }${saved.storage_slot ? ` · 櫃位 ${saved.storage_slot}` : ' · 未設櫃位'}`,
        { variant: 'success' }
      );
      setSlotRow(null);
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.error?.message || err.response?.data?.message || '櫃位設定失敗',
        { variant: 'error' }
      );
    } finally {
      setSlotSubmitting(false);
    }
  };

  // One-bit write from the 未上架 chip, no dialog. set-slot applies on_display
  // independently, so the 櫃位 is untouched.
  const quickSetDisplay = async (row: InventoryRow, onDisplay: boolean) => {
    try {
      const res = await api.post('/api/noke/inventory/set-slot', {
        variant_id: row.variant_id,
        on_display: onDisplay,
      });
      applyRowUpdate(res.data.item);
      enqueueSnackbar(onDisplay ? '已上架' : '已標記未上架', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || '設定失敗', { variant: 'error' });
    }
  };

  // 未上架 = nothing of this variant is out front. 未設櫃位 only counts rows that
  // still HAVE stock — a sold-out variant with no location is not a problem to
  // chase, and listing it would bury the ones that are.
  const visibleRows = useMemo(() => {
    if (filterMode === 'office') return rows.filter((r) => (r.qty_office ?? 0) > 0);
    if (filterMode === 'transit') return rows.filter((r) => (r.qty_in_transit ?? 0) > 0);
    if (filterMode === 'store') return rows.filter((r) => r.qty_on_hand > 0);
    // Sales now clear on_display automatically, so 未上架 IS the 補貨 worklist —
    // it must only hold rows there is actually something to fetch for. A sold-out
    // variant is correctly 未上架 and correctly not on this list.
    if (filterMode === 'offshelf') {
      return rows.filter((r) => r.on_display === false && r.qty_on_hand > 0);
    }
    if (filterMode === 'noslot') return rows.filter((r) => !r.storage_slot && r.qty_on_hand > 0);
    return rows;
  }, [rows, filterMode]);

  const offShelfCount = useMemo(
    () => rows.filter((r) => r.on_display === false && r.qty_on_hand > 0).length,
    [rows]
  );
  const noSlotCount = useMemo(
    () => rows.filter((r) => !r.storage_slot && r.qty_on_hand > 0).length,
    [rows]
  );

  // 依櫃位: one group per shelf, in physical walking order, for put-away and
  // 盤點. Only rows with stock — an empty variant is not in the cabinet.
  const slotGroups = useMemo(() => {
    const map = new Map<string, InventoryRow[]>();
    visibleRows
      .filter((r) => r.qty_on_hand > 0 && r.storage_slot)
      .forEach((r) => {
        const key = r.storage_slot as string;
        const list = map.get(key);
        if (list) list.push(r);
        else map.set(key, [r]);
      });
    return Array.from(map.entries())
      .sort((x, y) => {
        const [ax, bx] = slotSortKey(x[0]);
        const [ay, by] = slotSortKey(y[0]);
        return ax - ay || bx.localeCompare(by);
      })
      .map(([slot, items]) => ({
        slot,
        items,
        pieces: items.reduce((sum, it) => sum + it.qty_on_hand, 0),
      }));
  }, [visibleRows]);

  const reprint = (row: InventoryRow) => {
    if (!wearsHangTag(row)) {
      enqueueSnackbar('MdoC 商品不印吊牌 — 請到「中文標籤」分頁列印中文標籤', { variant: 'info' });
      return;
    }
    // eslint-disable-next-line no-alert
    const input = window.prompt(`列印 ${row.sku} 標籤，數量：`, '1');
    if (input == null) return;
    const copies = parseInt(input, 10);
    if (!copies || copies <= 0) {
      enqueueSnackbar('請輸入有效的列印數量', { variant: 'warning' });
      return;
    }
    printNokeLabels([
      {
        sku: row.sku,
        productTitle: row.product_title,
        color: row.color,
        size: row.size,
        price: row.price,
        // Encode the manufacturer barcode when present so a printed tag matches
        // the packaging; else the NK system code.
        barcode: effectiveBarcode(row),
        origin: row.origin,
        copies,
      },
    ]);
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              fullWidth
              size="small"
              label="搜尋 SKU / 品名"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchInventory(q, sortMode)}
            />
          </Grid>
          <Grid item>
            <Button variant="contained" startIcon={<SearchIcon />} onClick={() => fetchInventory(q, sortMode)}>
              搜尋
            </Button>
          </Grid>
          <Grid item>
            {/* The put-away flow. Deliberately the loudest button on the tab:
                it replaces 送往專櫃 → 確認收貨 → 設櫃位 → 上架 with one scan,
                and the four-step path is what left 129 in-store variants with
                no 櫃位 and 69 sitting 未上架. */}
            <Tooltip title="到店後掃吊牌，一次完成上架＋櫃位；反向可掃下架（移回辦公室）">
              <Button
                variant="contained"
                color="success"
                startIcon={<QrCodeScannerIcon />}
                onClick={() => setScanOpen(true)}
              >
                掃碼上架
              </Button>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="掃過一區（上架或某個櫃位）的所有吊牌，讓系統紀錄跟實際一致；不會改數量">
              <Button
                variant="outlined"
                color="success"
                startIcon={<FactCheckOutlinedIcon />}
                onClick={() => setReconcileOpen(true)}
              >
                盤點模式
              </Button>
            </Tooltip>
          </Grid>
          <Grid item>
            <Button
              variant={sortMode === 'velocity' ? 'contained' : 'outlined'}
              color="secondary"
              startIcon={<TrendingUpIcon />}
              onClick={toggleVelocitySort}
            >
              暢銷排序
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={viewMode === 'slots' ? 'contained' : 'outlined'}
              startIcon={<ViewModuleIcon />}
              onClick={() => {
                const next = viewMode === 'slots' ? 'list' : 'slots';
                setViewMode(next);
                // 依櫃位 groups BY slot while 未設櫃位 filters for rows that have
                // none — together they can only ever render empty. Drop the
                // filter rather than leaving the staffer at a dead end.
                if (next === 'slots' && ['noslot', 'office', 'transit'].includes(filterMode)) setFilterMode('store');
              }}
            >
              依櫃位
            </Button>
          </Grid>
          <Grid item>
            <Tooltip title="列印 A1–A5 / B1–B5 大字標籤，貼在層板前緣">
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => printShelfLabels()}>
                櫃位標籤
              </Button>
            </Tooltip>
          </Grid>
          <Grid item sx={{ ml: 'auto' }}>
            <CalibrationButton />
          </Grid>
        </Grid>

        {/* Shelf filters. 未上架 answers 「哪些沒擺出來」; 未設櫃位 is the
            housekeeping queue — stock the system can't locate. */}
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label={`全部 (${rows.length})`}
            size="small"
            color={filterMode === 'all' ? 'primary' : 'default'}
            variant={filterMode === 'all' ? 'filled' : 'outlined'}
            onClick={() => setFilterMode('all')}
          />
          {([
            { key: 'office', label: '辦公室', count: rows.filter((row) => (row.qty_office ?? 0) > 0).length },
            { key: 'transit', label: '運送中', count: rows.filter((row) => (row.qty_in_transit ?? 0) > 0).length },
            { key: 'store', label: '在櫃', count: rows.filter((row) => row.qty_on_hand > 0).length },
          ] as const).map(({ key, label, count }) => (
            <Chip key={key} label={`${label} (${count})`} size="small"
              color={filterMode === key ? 'primary' : 'default'}
              variant={filterMode === key ? 'filled' : 'outlined'}
              onClick={() => { setFilterMode(key); setViewMode('list'); }} />
          ))}
          <Chip
            label={`未上架 (${offShelfCount})`}
            size="small"
            color={filterMode === 'offshelf' ? 'primary' : 'default'}
            variant={filterMode === 'offshelf' ? 'filled' : 'outlined'}
            onClick={() => setFilterMode('offshelf')}
          />
          <Chip
            label={`未設櫃位 (${noSlotCount})`}
            size="small"
            color={filterMode === 'noslot' ? 'warning' : 'default'}
            variant={filterMode === 'noslot' ? 'filled' : 'outlined'}
            onClick={() => {
              setFilterMode('noslot');
              // See the 依櫃位 handler: this pair has no non-empty result.
              if (viewMode === 'slots') setViewMode('list');
            }}
          />
        </Box>
      </Paper>

      {/* Scans merge each row in place while the dialog is open (no spinner, no
          lost scroll); the refetch on close picks up the joined columns the
          mutation route doesn't return. */}
      <ScanShelveDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onApplied={applyRowUpdate}
        onDirtyClose={() => fetchInventory(q, sortMode)}
      />

      <ReconcileScanDialog
        open={reconcileOpen}
        onClose={(changed) => {
          setReconcileOpen(false);
          if (changed) {
            fetchInventory(q, sortMode);
            fetchLastReconciled();
          }
        }}
      />

      {currentUser && <NokeInventoryTransfer key={currentUser.id} userId={currentUser.id} rows={rows} disabled={loading}
        onComplete={() => { fetchInventory(q, sortMode); }} />}

      <Paper sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : visibleRows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            {rows.length === 0
              ? '目前沒有專櫃庫存'
              : filterMode === 'offshelf'
                ? '沒有待補貨的商品 — 櫃面都有擺'
                : filterMode === 'noslot' ? '所有有庫存的商品都已設定櫃位'
                  : '此位置目前沒有符合的庫存'}
          </Box>
        ) : viewMode === 'slots' ? (
          // 依櫃位 — the put-away / 盤點 view: walk the cabinet shelf by shelf and
          // check what should be on each one. Same idea as the receiving page's
          // 依貨架格 tab, and like it, grouped client-side from rows already held.
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              上架 {reconciledLabel('display') || '尚未重盤'}
            </Typography>
            {slotGroups.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                還沒有商品設定櫃位 — 在「庫存」列表點「位置」欄即可設定
              </Box>
            ) : (
              <Grid container spacing={2}>
                {slotGroups.map((group) => (
                  <Grid item key={group.slot} xs={12} sm={6} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                          {group.slot}
                        </Typography>
                        <Chip size="small" label={`${group.items.length} 款 / ${group.pieces} 件`} />
                        {reconciledLabel(`slot:${group.slot}`) && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            {reconciledLabel(`slot:${group.slot}`)}
                          </Typography>
                        )}
                      </Box>
                      <Divider sx={{ mb: 1 }} />
                      {group.items.map((it) => (
                        <Box
                          key={it.variant_id}
                          onClick={() => openSlotEdit(it)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            cursor: 'pointer',
                          }}
                        >
                          <Thumb src={it.image_url} alt={it.product_title} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" noWrap title={it.product_title}>
                              {it.product_title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {it.sku} · {it.color} / {it.size}
                              {it.on_display === false && ' · 未上架'}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            ×{it.qty_on_hand}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        ) : (
          // TableContainer scrolls horizontally on narrow phones; minWidth
          // keeps the columns readable instead of cramming.
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 960 }}>
              <TableHead>
                <TableRow>
                  <TableCell>圖</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>條碼</TableCell>
                  <TableCell>品名</TableCell>
                  <TableCell>顏色</TableCell>
                  <TableCell>尺寸</TableCell>
                  <TableCell align="right">價格</TableCell>
                  <TableCell align="right">辦公室</TableCell>
                  <TableCell align="right">運送中</TableCell>
                  <TableCell align="right">在櫃數量</TableCell>
                  <TableCell>專櫃櫃位／上架</TableCell>
                  <TableCell align="right">近7天售出</TableCell>
                  <TableCell align="right">近30天售出</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, idx) => {
                  const hot = (row.sold_7d ?? 0) >= VELOCITY_CUE_THRESHOLD;
                  // The API returns siblings of one SKU contiguously (size then
                  // colour). Draw a rule at each SKU change so a product reads as
                  // one block — 「這件還有沒有 M」 is answered by looking down a
                  // group, not by scanning the whole table.
                  const groupStart = idx === 0 || visibleRows[idx - 1].sku !== row.sku;
                  return (
                    <TableRow
                      key={row.variant_id}
                      // Scanned row highlight (search-by-scan during stocktakes).
                      sx={{
                        ...(row.variant_id === highlightVariantId
                          ? { bgcolor: 'rgba(76, 94, 138, 0.14)' }
                          : null),
                        ...(groupStart && idx > 0
                          ? { '& > td': { borderTop: '2px solid rgba(0, 0, 0, 0.18)' } }
                          : null),
                      }}
                    >
                      <TableCell>
                        <Thumb src={row.image_url} alt={row.product_title} onClick={() => openGallery(row)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: groupStart ? 600 : 400,
                              color: groupStart ? 'text.primary' : 'text.disabled',
                            }}
                          >
                            {row.sku}
                          </Typography>
                          {row.is_custom && (
                            <Chip size="small" color="warning" label="自訂" sx={{ height: 18 }} />
                          )}
                        </Box>
                        {/* Receiving-page 標籤 (Korean source store) — tells staff
                            where this SKU is reordered from. Once per block. */}
                        {groupStart && row.source_tag && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                            {row.source_tag}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            component="span"
                            sx={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                          >
                            {effectiveBarcode(row)}
                          </Typography>
                          {row.custom_barcode && row.custom_barcode.trim() && (
                            <Chip size="small" color="info" label="原廠" sx={{ height: 18 }} />
                          )}
                        </Box>
                      </TableCell>
                      {/* 品名 — links to the live metanoia.tw PDP when the row is
                          a catalog product, so staff can pull up photos, 尺寸 and
                          the 官網 price at the counter. Custom items (and any row
                          whose product isn't on the storefront) carry no handle
                          and stay plain text — never fabricate a link. */}
                      <TableCell>
                        {row.handle ? (
                          <Link
                            href={pdpUrl(row.handle)}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.25,
                              fontWeight: 500,
                            }}
                          >
                            {row.product_title}
                            <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                          </Link>
                        ) : (
                          row.product_title
                        )}{' '}
                        <Chip size="small" label={vendorBucket(row.vendor)} sx={{ ml: 0.5 }} />
                      </TableCell>
                      <TableCell>{row.color}</TableCell>
                      <TableCell>{row.size}</TableCell>
                      <TableCell align="right">{formatNT(row.price)}</TableCell>
                      <TableCell align="right">{row.qty_office ?? 0}</TableCell>
                      <TableCell align="right">{row.qty_in_transit ?? 0}</TableCell>
                      <TableCell align="right">
                        <Typography
                          component="span"
                          sx={{ fontWeight: 600, color: row.qty_on_hand <= 0 ? 'error.main' : 'text.primary' }}
                        >
                          {row.qty_on_hand}
                        </Typography>
                      </TableCell>
                      {/* 位置 — click to set 櫃位 / 上架. The whole cell is the
                          target (a 3mm icon is not a phone-friendly hit area). */}
                      <TableCell
                        onClick={() => openSlotEdit(row)}
                        sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {row.storage_slot ? (
                            <Chip
                              size="small"
                              icon={<Inventory2OutlinedIcon />}
                              label={row.storage_slot}
                              sx={{ height: 22, fontWeight: 700 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              未設定
                            </Typography>
                          )}
                          {/* ALWAYS render the shelf state, both ways. When 已上架
                              was only the ABSENCE of a chip, flipping a row ON
                              changed nothing on screen and the save read as a
                              no-op (Magnus 2026-09-04). A confirmation you have
                              to infer from a missing element is not one. */}
                          {row.qty_on_hand <= 0 ? (
                            // 「尚無在櫃庫存」 was true but useless when the pieces are
                            // sitting in the office waiting to be carried over — say
                            // which, so the cell (which opens 設定櫃位) is the next step.
                            <Typography variant="caption" color={(row.qty_office ?? 0) > 0 ? 'warning.main' : 'text.secondary'}>
                              {(row.qty_office ?? 0) > 0 ? `辦公室 ${row.qty_office} 件 · 待帶到門市` : '尚無在櫃庫存'}
                            </Typography>
                          ) : row.on_display === false ? (
                            // Clickable: 未上架 is a to-do, and the fix is one bit.
                            // stopPropagation so it doesn't also open the dialog
                            // behind it — the cell's job stays 設定櫃位.
                            <Chip
                              size="small"
                              color="warning"
                              label={row.qty_on_hand > 0 ? '未上架 · 補好了' : '未上架'}
                              clickable={row.qty_on_hand > 0}
                              onClick={
                                row.qty_on_hand > 0
                                  ? (e) => {
                                      e.stopPropagation();
                                      quickSetDisplay(row, true);
                                    }
                                  : undefined
                              }
                              sx={{ height: 20 }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label="已上架"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography component="span" sx={{ fontWeight: hot ? 700 : 400 }}>
                            {row.sold_7d ?? 0}
                          </Typography>
                          {hot && (
                            <Chip
                              size="small"
                              color="secondary"
                              label="▲"
                              sx={{ height: 18, '& .MuiChip-label': { px: 0.5 } }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{row.sold_30d ?? 0}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          <Button size="small" onClick={() => openAdjust(row)} sx={{ mr: 0.5 }}>
                            調整
                          </Button>
                          <IconButton size="small" onClick={() => openBarcodeEdit(row)} title="編輯條碼">
                            <QrCodeIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => reprint(row)}
                            disabled={!wearsHangTag(row)}
                            title={wearsHangTag(row) ? '列印標籤' : 'MdoC 不印吊牌，請用「中文標籤」分頁'}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                          {/* Writing stock off entirely is an owner action —
                              門市人員 correct a miscount with 調整, which leaves a
                              movement row. The backend enforces this too
                              (requireAdminRole on DELETE /inventory/:id). */}
                          {isAdmin && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteRow(row)}
                              title="刪除"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* 櫃位 / 上架狀態. Suggested shelves plus free entry for custom codes. */}
      <Dialog open={Boolean(slotRow)} onClose={() => setSlotRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>設定櫃位</DialogTitle>
        <DialogContent>
          {slotRow && (
            <DialogContentText sx={{ mb: 2 }}>
              {slotRow.product_title} — {slotRow.color}
              {slotRow.size ? ` / ${slotRow.size}` : ''} (在櫃 {slotRow.qty_on_hand} 件
              {(slotRow.qty_office ?? 0) > 0 ? ` · 辦公室 ${slotRow.qty_office} 件` : ''})
            </DialogContentText>
          )}
          <Autocomplete
            freeSolo
            fullWidth
            size="small"
            options={NOKE_SLOTS}
            value={slotValue}
            inputValue={slotValue}
            onInputChange={(_, value) => setSlotValue(normalizeSlot(value) || '')}
            onChange={(_, value) => setSlotValue(normalizeSlot(value) || '')}
            disabled={slotSubmitting}
            sx={{ mb: 2 }}
            renderInput={(params) => (
              <TextField {...params} label="櫃位"
                helperText="可選 A1–A10、B1–B10，或輸入 C1、A11、A3-2 等；清空即取消設定"
                inputProps={{ ...params.inputProps, maxLength: 16 }} />
            )}
          />
          <FormControlLabel
            control={
              <Switch checked={slotOnDisplay} onChange={(e) => setSlotOnDisplay(e.target.checked)} />
            }
            label={slotOnDisplay ? '已上架 (櫃面有擺)' : '未上架 (全部收在櫃內)'}
          />
          {/* 已上架 on office-only stock means 「我已經把貨帶到門市了」. Saying so used
              to be refused, so it went unsaid and the POS later sold stock the
              system still believed was in the office. Offer the move here. */}
          {slotRow && slotOnDisplay && slotRow.qty_on_hand <= 0 && (slotRow.qty_office ?? 0) > 0 && (
            <Box sx={{ mt: 2 }}>
              <TextField
                type="number"
                size="small"
                fullWidth
                label="從辦公室移入幾件"
                value={slotMoveIn}
                disabled={slotSubmitting}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setSlotMoveIn(Number.isFinite(n) ? Math.max(0, Math.min(n, slotRow.qty_office ?? 0)) : 0);
                }}
                inputProps={{ min: 0, max: slotRow.qty_office ?? 0 }}
                helperText={`辦公室有 ${slotRow.qty_office} 件。勾了已上架就是「我已經帶到門市」，儲存時一併移入在櫃。`}
              />
            </Box>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            {slotRow && slotOnDisplay && slotRow.qty_on_hand <= 0 && (slotRow.qty_office ?? 0) > 0
              ? '在櫃數量是櫃面＋櫃內的總數。移入 0 件就只改上架狀態，數量不變。'
              : '在櫃數量是櫃面＋櫃內的總數，設定櫃位不會改變數量。'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlotRow(null)}>取消</Button>
          <Button variant="contained" onClick={submitSlot} disabled={slotSubmitting}>
            儲存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(adjustRow)} onClose={() => setAdjustRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>調整庫存</DialogTitle>
        <DialogContent>
          {adjustRow && (
            <DialogContentText sx={{ mb: 2 }}>
              {adjustRow.product_title} — {adjustRow.color}
              {adjustRow.size ? ` / ${adjustRow.size}` : ''}
              （{adjustLocation === 'office' ? '辦公室' : '在櫃'}目前 {adjustLocation === 'office' ? adjustRow.qty_office ?? 0 : adjustRow.qty_on_hand} 件）
            </DialogContentText>
          )}
          <TextField select fullWidth size="small" label="調整位置" value={adjustLocation} sx={{ mb: 2 }}
            disabled={adjustSubmitting} onChange={(e) => setAdjustLocation(e.target.value as NokeStockLocation)}
            helperText="只用於盤點差異。位置移動請使用送往專櫃、確認收貨或移回辦公室。">
            <MenuItem value="office">辦公室</MenuItem>
            <MenuItem value="store">NOKE 在櫃</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton onClick={() => setAdjustDelta(String((parseInt(adjustDelta, 10) || 0) - 1))}>
              <RemoveIcon />
            </IconButton>
            <TextField
              label="調整數量 (+/-)"
              type="number"
              value={adjustDelta}
              onChange={(e) => setAdjustDelta(e.target.value)}
              size="small"
              inputProps={{ style: { textAlign: 'center' } }}
            />
            <IconButton onClick={() => setAdjustDelta(String((parseInt(adjustDelta, 10) || 0) + 1))}>
              <AddIcon />
            </IconButton>
          </Box>
          <TextField
            label="備註 (選填)"
            value={adjustNote}
            onChange={(e) => setAdjustNote(e.target.value)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustRow(null)}>取消</Button>
          <Button variant="contained" onClick={submitAdjust} disabled={adjustSubmitting}>
            {adjustSubmitting ? <CircularProgress size={20} /> : '確認調整'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product gallery dialog — full image set for a variant. */}
      <Dialog open={Boolean(galleryRow)} onClose={() => setGalleryRow(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{galleryRow?.product_title || '商品圖片'}</span>
          <IconButton size="small" onClick={() => setGalleryRow(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {galleryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : galleryError ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>載入圖片失敗，請稍後再試</Box>
          ) : galleryImages.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>沒有商品圖片</Box>
          ) : (
            <Grid container spacing={1}>
              {galleryImages
                .slice()
                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                .map((img, idx) => (
                  <Grid item xs={6} sm={4} md={3} key={`${img.src}-${idx}`}>
                    <a href={img.src} target="_blank" rel="noopener noreferrer">
                      <Box
                        sx={{
                          width: '100%',
                          pt: '125%',
                          position: 'relative',
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: 'grey.100',
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        <img
                          src={img.src}
                          alt={`${galleryRow?.product_title || ''} ${idx + 1}`}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    </a>
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom (manufacturer) barcode edit dialog. The input is a normal
          focusable text field, so staff can scan the packaging barcode straight
          into it with the hardware scanner (types digits + Enter). */}
      <Dialog open={Boolean(barcodeRow)} onClose={() => setBarcodeRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>編輯原廠條碼</DialogTitle>
        <DialogContent>
          {barcodeRow && (
            <DialogContentText sx={{ mb: 2 }}>
              {barcodeRow.product_title} — {barcodeRow.color}
              {barcodeRow.size ? ` / ${barcodeRow.size}` : ''}
            </DialogContentText>
          )}
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="原廠條碼"
            placeholder="掃描包裝上的條碼，或手動輸入"
            value={barcodeValue}
            onChange={(e) => setBarcodeValue(e.target.value)}
            onKeyDown={(e) => {
              // A scanner ends its burst with Enter — commit on Enter.
              if (e.key === 'Enter') submitBarcode(false);
            }}
            helperText="留空並按「清除」可移除原廠條碼，改回系統 NK 條碼"
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button color="error" onClick={() => submitBarcode(true)} disabled={barcodeSubmitting}>
            清除
          </Button>
          <Box>
            <Button onClick={() => setBarcodeRow(null)} sx={{ mr: 1 }}>
              取消
            </Button>
            <Button
              variant="contained"
              onClick={() => submitBarcode(false)}
              disabled={barcodeSubmitting || !barcodeValue.trim()}
            >
              {barcodeSubmitting ? <CircularProgress size={20} /> : '儲存'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Remove-from-inventory confirm dialog. */}
      <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>從專櫃庫存移除</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>確定要從專櫃庫存移除？</DialogContentText>
          {deleteRow && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {deleteRow.product_title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteRow.color}
                {deleteRow.size ? ` / ${deleteRow.size}` : ''} · 在櫃數量 {deleteRow.qty_on_hand}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="error">
            此操作無法復原。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)}>取消</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleteSubmitting}>
            {deleteSubmitting ? <CircularProgress size={20} /> : '移除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// =============================================================================
// Tab 3 — 銷售紀錄
// =============================================================================

const SaleRow: React.FC<{ sale: Sale; onReverse: (sale: Sale) => void }> = ({ sale, onReverse }) => {
  const [open, setOpen] = useState(false);
  const reversed = isReversed(sale);
  const itemCount = (sale.items || []).reduce((s, it) => s + (it.qty || 0), 0);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, opacity: reversed ? 0.6 : 1 }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ textDecoration: reversed ? 'line-through' : 'none' }}>{sale.sale_ref}</TableCell>
        <TableCell>{formatDateTime(sale.sold_at)}</TableCell>
        <TableCell align="right">{itemCount}</TableCell>
        <TableCell align="right" sx={{ textDecoration: reversed ? 'line-through' : 'none' }}>
          {formatNT(sale.net_total)}
        </TableCell>
        <TableCell>{sale.customer_name || '—'}</TableCell>
        <TableCell>{sale.salesperson || '—'}</TableCell>
        <TableCell>
          {reversed ? (
            <Chip size="small" color="default" label="已取消" />
          ) : (
            <Chip size="small" color="success" label="完成" />
          )}
        </TableCell>
        <TableCell align="center">
          {!reversed && (
            <Button size="small" color="error" onClick={() => onReverse(sale)}>
              取消銷售
            </Button>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>圖</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>品名</TableCell>
                    <TableCell>顏色 / 尺寸</TableCell>
                    <TableCell align="right">單價</TableCell>
                    <TableCell align="right">數量</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(sale.items || []).map((it, idx) => (
                    <TableRow key={`${it.variant_id}-${idx}`}>
                      <TableCell sx={{ width: 48 }}>
                        <Thumb src={it.image_url} alt={it.product_title || ''} size={38} />
                      </TableCell>
                      <TableCell>{it.sku || it.variant_id}</TableCell>
                      <TableCell>{it.product_title || '—'}</TableCell>
                      <TableCell>
                        {it.color || ''}
                        {it.size ? ` / ${it.size}` : ''}
                      </TableCell>
                      <TableCell align="right">{formatNT(it.unit_price)}</TableCell>
                      <TableCell align="right">{it.qty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ mt: 1 }}>
                <VendorSplit totals={sale.vendor_totals} dense />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const SALES_PAGE_SIZE = 50;

const SalesTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sales, setSales] = useState<Sale[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [reverseTarget, setReverseTarget] = useState<Sale | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reversing, setReversing] = useState(false);

  // append=false reloads page 1 (查詢 / after a reversal); append=true fetches
  // the next page and appends (載入更多) so a busy range is never truncated.
  const fetchSales = useCallback(
    async (pageArg: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const res = await api.get('/api/noke/sales', {
          params: { from, to, page: pageArg, limit: SALES_PAGE_SIZE },
        });
        const fetched: Sale[] = res.data.sales || [];
        setTotal(res.data.total ?? fetched.length);
        setPage(pageArg);
        setSales((prev) => (append ? [...prev, ...fetched] : fetched));
      } catch (err: any) {
        enqueueSnackbar(err.response?.data?.message || '載入銷售紀錄失敗', { variant: 'error' });
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [from, to, enqueueSnackbar]
  );

  useEffect(() => {
    fetchSales(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmReverse = async () => {
    if (!reverseTarget) return;
    if (reverseReason.trim().length < 2) return;
    setReversing(true);
    try {
      await api.post(`/api/noke/sales/${reverseTarget.id}/reverse`, { reason: reverseReason.trim() });
      enqueueSnackbar(`已取消銷售 ${reverseTarget.sale_ref}`, { variant: 'success' });
      setReverseTarget(null);
      setReverseReason('');
      fetchSales(1, false);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || '取消銷售失敗', { variant: 'error' });
    } finally {
      setReversing(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              label="起"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <TextField
              label="迄"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <Button variant="contained" startIcon={<SearchIcon />} onClick={() => fetchSales(1, false)}>
              查詢
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : sales.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>此區間沒有銷售紀錄</Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>單號</TableCell>
                  <TableCell>時間</TableCell>
                  <TableCell align="right">件數</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell>會員</TableCell>
                  <TableCell>銷售人員</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.map((sale) => (
                  <SaleRow key={sale.id} sale={sale} onReverse={setReverseTarget} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && sales.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderTop: '1px solid #eee',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              顯示 {sales.length} / 共 {total} 筆
            </Typography>
            {sales.length < total && (
              <Button
                size="small"
                onClick={() => fetchSales(page + 1, true)}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={16} /> : null}
              >
                載入更多
              </Button>
            )}
          </Box>
        )}
      </Paper>

      <Dialog
        open={Boolean(reverseTarget)}
        onClose={() => {
          setReverseTarget(null);
          setReverseReason('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>取消銷售</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要取消銷售 {reverseTarget?.sale_ref}（{formatNT(reverseTarget?.net_total)}）嗎？
            此操作會將售出的商品退回專櫃庫存。
          </DialogContentText>
          {/* Required: a void moves money and stock. 原因 + 操作人 are stored on
              the sale so 日結 對帳 差異 can be traced instead of guessed. */}
          <TextField
            label="取消原因（必填）"
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            placeholder="例如：客人改變心意 / 刷錯商品 / 尺寸不合換貨"
            fullWidth
            autoFocus
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 200 }}
            helperText="會記錄在這筆銷售上，供日結對帳查核"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReverseTarget(null);
              setReverseReason('');
            }}
          >
            返回
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmReverse}
            disabled={reversing || reverseReason.trim().length < 2}
          >
            {reversing ? <CircularProgress size={20} /> : '確認取消'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// =============================================================================
// Tab 4 — 日結
// =============================================================================

const DailyTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [date, setDate] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<DailyStats | null>(null);

  const fetchStats = useCallback(
    async (d: string) => {
      setLoading(true);
      try {
        const res = await api.get('/api/noke/stats/daily', { params: { date: d } });
        setStats(res.data);
      } catch (err: any) {
        enqueueSnackbar(err.response?.data?.message || '載入日結失敗', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchStats(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bigNumbers: Array<{ label: string; value: string }> = stats
    ? [
        { label: '交易筆數', value: String(stats.tx_count) },
        { label: '件數', value: String(stats.items_sold) },
        { label: '總額 (折扣前)', value: formatNT(stats.gross) },
        { label: '折扣', value: formatNT(stats.discount) },
        { label: '淨額', value: formatNT(stats.net_total) },
      ]
    : [];

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              label="日期"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <Button variant="contained" startIcon={<SearchIcon />} onClick={() => fetchStats(date)}>
              查詢
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : stats ? (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {bigNumbers.map((n) => (
              <Grid item xs={6} sm={4} md={2.4} key={n.label}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {n.label}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {n.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* MdoC 連帶率 — pre-registered kill-gate metric (target ≥15%). Kept
              legible, not buried in the number strip. */}
          {(() => {
            const txCount = stats.tx_count || 0;
            const mdocTx = stats.mdoc_tx_count ?? 0;
            const rate =
              stats.mdoc_attach_rate != null
                ? stats.mdoc_attach_rate
                : txCount > 0
                  ? mdocTx / txCount
                  : 0;
            const pct = Math.round(rate * 100);
            const onTarget = rate >= 0.15;
            return (
              <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderColor: onTarget ? 'success.main' : 'warning.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    MdoC 連帶率
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: onTarget ? 'success.main' : 'warning.main' }}
                  >
                    {pct}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {mdocTx} / {txCount} 筆
                  </Typography>
                  <Chip
                    size="small"
                    color={onTarget ? 'success' : 'default'}
                    label={onTarget ? '達標 (≥15%)' : '目標 ≥15%'}
                  />
                </Box>
              </Paper>
            );
          })()}

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              品牌分帳 (對帳用)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <VendorSplit totals={stats.vendor_totals} />
          </Paper>
        </>
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>無資料</Box>
      )}
    </Box>
  );
};

// =============================================================================
// Tab — 中文標籤 (imported-cosmetics compliance stickers)
// =============================================================================

// One stored 中文標籤 row (mirrors the /api/noke/cn-labels contract).
interface CnLabelRow extends CnLabelData {
  id: number;
  /** Manufacturer EAN on the packaging. Reference copy — not printed. */
  barcode?: string | null;
  /** true/false for a 13-digit code, null when it isn't EAN-13. */
  barcode_valid?: boolean | null;
  label_size: CnLabelSize;
  note?: string | null;
  is_active: boolean;
  /** Legally-required fields still blank on this row, computed server-side. */
  gaps: string[];
  updated_at?: string;
}

// Server-supplied defaults for a new label (importer block, standard 注意事項).
interface CnLabelDefaults {
  importer_name: string;
  importer_addr: string;
  importer_tel: string;
  origin: string;
  caution_zh: string;
  lot_no: string;
  label_size: CnLabelSize;
}

const EMPTY_CN_FORM = {
  sku: '',
  product_name: '',
  variant_name: '',
  use_zh: '',
  usage_zh: '',
  net_content: '',
  net_label: '淨含量',
  ingredients: '',
  caution_zh: '',
  manufacturer: '',
  manufacturer_addr: '',
  origin: '',
  importer_name: '',
  importer_addr: '',
  importer_tel: '',
  shelf_life: '',
  expiry: '',
  lot_no: '如產品包裝所示',
  barcode: '',
  license_no: '',
  label_size: DEFAULT_CN_LABEL_SIZE as CnLabelSize,
  note: '',
  is_active: true,
};

type CnLabelForm = typeof EMPTY_CN_FORM;

// 中文標籤 print-position calibration. Separate stock and separate roll from the
// hang-tag, so it keeps its own saved offset rather than sharing that one.
const CnCalibrationButton: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [size, setSize] = useState<CnLabelSize>(DEFAULT_CN_LABEL_SIZE);
  // A 45x60 roll and a 10x12cm piece feed differently, so the test print has to
  // come out on whichever one is loaded — not always the single-label page.
  const [sheet, setSheet] = useState<CnSheetMode>(DEFAULT_CN_SHEET_MODE);

  const handleOpen = () => {
    const { offsetX, offsetY } = readCnLabelOffset();
    setX(offsetX);
    setY(offsetY);
    setSheet(readCnSheetMode());
    setOpen(true);
  };

  const persist = (nx: number, ny: number) => {
    localStorage.setItem(CN_OFFSET_X_KEY, String(nx));
    localStorage.setItem(CN_OFFSET_Y_KEY, String(ny));
  };

  const field = (label: string, value: number, setValue: (n: number) => void) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <IconButton size="small" onClick={() => setValue(clampOffset(value - 0.5))}>
        <RemoveIcon fontSize="small" />
      </IconButton>
      <TextField
        label={label}
        type="number"
        size="small"
        value={value}
        onChange={(e) => setValue(clampOffset(parseFloat(e.target.value) || 0))}
        inputProps={{ step: 0.5, min: -10, max: 10, style: { textAlign: 'center' } }}
      />
      <IconButton size="small" onClick={() => setValue(clampOffset(value + 0.5))}>
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <>
      <Button size="small" startIcon={<TuneIcon />} onClick={handleOpen}>
        列印校正
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>中文標籤列印校正</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            印出來偏左 → X 調大；偏上 → Y 調大。單位為公釐 (mm)。
          </DialogContentText>
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>測試尺寸</InputLabel>
            <Select label="測試尺寸" value={size} onChange={(e) => setSize(e.target.value as CnLabelSize)}>
              {(Object.keys(CN_LABEL_SIZES) as CnLabelSize[]).map((s) => (
                <MenuItem key={s} value={s}>
                  {CN_LABEL_SIZES[s].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>紙張</InputLabel>
            <Select label="紙張" value={sheet} onChange={(e) => setSheet(e.target.value as CnSheetMode)}>
              {(Object.keys(CN_SHEET_MODES) as CnSheetMode[]).map((m) => (
                <MenuItem key={m} value={m}>
                  {CN_SHEET_MODES[m].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {field('X 位移 (mm)', x, setX)}
          {field('Y 位移 (mm)', y, setY)}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => printCnCalibrationLabel(size, x, y, sheet)}
            sx={{ mt: 1 }}
          >
            測試列印
          </Button>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            color="inherit"
            onClick={() => {
              setX(0);
              setY(0);
              persist(0, 0);
              enqueueSnackbar('列印校正已歸零', { variant: 'info' });
            }}
          >
            歸零
          </Button>
          <Box>
            <Button onClick={() => setOpen(false)} sx={{ mr: 1 }}>
              取消
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                persist(x, y);
                enqueueSnackbar('列印校正已儲存', { variant: 'success' });
                setOpen(false);
              }}
            >
              儲存
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Create / edit one 中文標籤. Every field on the sticker is editable here,
// because the content is the legal text — nothing about it is derivable from
// the storefront catalog (成分 and 許可證字號 come off the supplier's dossier).
const CnLabelEditor: React.FC<{
  open: boolean;
  editing: CnLabelRow | null;
  defaults: CnLabelDefaults | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, editing, defaults, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<CnLabelForm>(EMPTY_CN_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Reset the form each time the dialog opens: an existing row loads verbatim,
  // a new one is seeded with the importer block + standard 注意事項 so staff
  // never retype the boilerplate (or mistype the address on a legal label).
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        sku: editing.sku || '',
        product_name: editing.product_name || '',
        variant_name: editing.variant_name || '',
        use_zh: editing.use_zh || '',
        usage_zh: editing.usage_zh || '',
        net_content: editing.net_content || '',
        net_label: editing.net_label || '淨含量',
        ingredients: editing.ingredients || '',
        caution_zh: editing.caution_zh || '',
        manufacturer: editing.manufacturer || '',
        manufacturer_addr: editing.manufacturer_addr || '',
        origin: editing.origin || '',
        importer_name: editing.importer_name || '',
        importer_addr: editing.importer_addr || '',
        importer_tel: editing.importer_tel || '',
        shelf_life: editing.shelf_life || '',
        expiry: editing.expiry || '',
        lot_no: editing.lot_no || '',
        barcode: editing.barcode || '',
        license_no: editing.license_no || '',
        label_size: isCnLabelSize(editing.label_size) ? editing.label_size : DEFAULT_CN_LABEL_SIZE,
        note: editing.note || '',
        is_active: editing.is_active !== false,
      });
    } else {
      setForm({
        ...EMPTY_CN_FORM,
        origin: defaults?.origin || '',
        lot_no: defaults?.lot_no || '如產品包裝所示',
        caution_zh: defaults?.caution_zh || '',
        importer_name: defaults?.importer_name || '',
        importer_addr: defaults?.importer_addr || '',
        importer_tel: defaults?.importer_tel || '',
        label_size: defaults?.label_size || DEFAULT_CN_LABEL_SIZE,
      });
    }
  }, [open, editing, defaults]);

  const set = <K extends keyof CnLabelForm>(key: K, value: CnLabelForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const text = (
    key: keyof CnLabelForm,
    label: string,
    opts: { rows?: number; placeholder?: string; helper?: string } = {}
  ) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={String(form[key] ?? '')}
      onChange={(e) => set(key, e.target.value as never)}
      multiline={Boolean(opts.rows)}
      minRows={opts.rows}
      placeholder={opts.placeholder}
      helperText={opts.helper}
    />
  );

  const submit = async () => {
    if (!form.sku.trim()) {
      enqueueSnackbar('請輸入 SKU', { variant: 'warning' });
      return;
    }
    if (!form.product_name.trim()) {
      enqueueSnackbar('請輸入品名', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/api/noke/cn-labels/${editing.id}`, form);
        enqueueSnackbar('中文標籤已更新', { variant: 'success' });
      } else {
        await api.post('/api/noke/cn-labels', form);
        enqueueSnackbar('中文標籤已建立', { variant: 'success' });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      // 409 = this SKU/款式 already has a label; 403 = counter role, not admin.
      enqueueSnackbar(err.response?.data?.message || '儲存失敗', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Live proof of what will print, from the same builder the printer uses.
  const previewCurrent = () =>
    previewNokeCnLabels([{ ...(form as unknown as CnLabelData), copies: 1 }]);

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `編輯中文標籤 — ${editing.sku}` : '新增中文標籤'}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          化粧品衛生安全管理法要求的標示項目：品名、用途、用法、淨重/容量、全成分、
          使用注意事項、製造或輸入業者名稱地址電話、原產地、保存期限、批號；
          防曬等特定用途化粧品另需標示許可證字號。
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            {text('sku', 'SKU *', { placeholder: 'M01' })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {text('product_name', '品名 *', { placeholder: '多功能遮瑕自然亮膚BB棒' })}
          </Grid>
          <Grid item xs={12} sm={3}>
            {text('variant_name', '款式 (選填)', { placeholder: '21號 自然色', helper: '同 SKU 多款時填' })}
          </Grid>

          <Grid item xs={12} sm={6}>
            {text('use_zh', '用途', { placeholder: '修飾膚色、遮瑕' })}
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>標示用詞</InputLabel>
              <Select label="標示用詞" value={form.net_label || '淨含量'}
                onChange={(e) => set('net_label', e.target.value)}>
                {['淨含量', '淨重', '容量', '數量'].map((w) => (
                  <MenuItem key={w} value={w}>{w}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            {text('net_content', '淨重 / 容量', { placeholder: '13g' })}
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>標籤尺寸</InputLabel>
              <Select
                label="標籤尺寸"
                value={form.label_size}
                onChange={(e) => set('label_size', e.target.value as CnLabelSize)}
              >
                {(Object.keys(CN_LABEL_SIZES) as CnLabelSize[]).map((s) => (
                  <MenuItem key={s} value={s}>
                    {CN_LABEL_SIZES[s].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            {text('usage_zh', '用法及保存方法', { rows: 2, placeholder: '取適量塗抹於臉部，均勻推開。' })}
          </Grid>
          <Grid item xs={12}>
            {text('ingredients', '全成分', {
              rows: 5,
              placeholder: '水、環五聚二甲基矽氧烷、二氧化鈦…',
              helper: '照供應商 INCI 中文全成分逐字輸入。字級會自動縮到放得下為止；仍放不下會在預覽標紅框。',
            })}
          </Grid>
          <Grid item xs={12}>
            {text('caution_zh', '使用注意事項', { rows: 4 })}
          </Grid>

          <Grid item xs={12} sm={6}>
            {text('manufacturer', '製造商', { placeholder: '（韓國）製造廠名稱' })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {text('manufacturer_addr', '製造商地址')}
          </Grid>

          <Grid item xs={12} sm={3}>
            {text('origin', '原產地', { placeholder: '韓國 (Republic of Korea)' })}
          </Grid>
          <Grid item xs={12} sm={3}>
            {text('shelf_life', '保存期限', { placeholder: '4年' })}
          </Grid>
          <Grid item xs={12} sm={3}>
            {text('expiry', '有效期限', {
              placeholder: '產品包裝所示',
              helper: '供應商寫「製造日期」時也填這裡',
            })}
          </Grid>
          <Grid item xs={12} sm={3}>
            {text('lot_no', '批號', { helper: '一律「如產品包裝所示」' })}
          </Grid>

          <Grid item xs={12} sm={6}>
            {text('license_no', '許可證字號 (特定用途)', {
              placeholder: '衛部粧輸字第 XXXXXX 號',
              helper: '防曬 / 美白等特定用途化粧品必填',
            })}
          </Grid>
          <Grid item xs={12} sm={3}>
            {text('barcode', '原廠條碼 (EAN)', {
              placeholder: '8809503112225',
              helper: '包裝上的條碼，不印在標籤；進貨時比對用',
            })}
          </Grid>
          <Grid item xs={12} sm={3}>
            {text('note', '內部備註 (不印在標籤)')}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              輸入業者（法規要求名稱、地址、電話）
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            {text('importer_name', '進口商名稱')}
          </Grid>
          <Grid item xs={12} sm={5}>
            {text('importer_addr', '進口商地址')}
          </Grid>
          <Grid item xs={12} sm={3}>
            {text('importer_tel', '服務電話')}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
              }
              label="啟用（停用後不出現在列印清單）"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button startIcon={<VisibilityOutlinedIcon />} onClick={previewCurrent}>
          預覽
        </Button>
        <Box>
          <Button onClick={onClose} disabled={submitting} sx={{ mr: 1 }}>
            取消
          </Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} color="inherit" /> : '儲存'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const CnLabelTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const isAdmin = !isCounterRole(currentUser?.role);

  const [rows, setRows] = useState<CnLabelRow[]>([]);
  const [defaults, setDefaults] = useState<CnLabelDefaults | null>(null);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [qtyById, setQtyById] = useState<Record<number, number>>({});
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CnLabelRow | null>(null);
  // Which physical stock is loaded. Persisted, because it changes rarely and
  // getting it wrong wastes a whole piece of label paper.
  const [sheet, setSheet] = useState<CnSheetMode>(DEFAULT_CN_SHEET_MODE);

  useEffect(() => setSheet(readCnSheetMode()), []);

  const chooseSheet = (next: CnSheetMode) => {
    setSheet(next);
    localStorage.setItem(CN_SHEET_KEY, next);
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/noke/cn-labels', {
        params: showInactive ? { include_inactive: '1' } : undefined,
      });
      setRows(res.data.labels || []);
      setDefaults(res.data.defaults || null);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || '載入中文標籤失敗', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, showInactive]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const setQty = (id: number, qty: number) =>
    setQtyById((prev) => ({ ...prev, [id]: Math.max(0, qty) }));

  const toLabelData = (row: CnLabelRow, copies: number): CnLabelData => ({ ...row, copies });

  const selected = useMemo(
    () => rows.map((r) => ({ row: r, qty: qtyById[r.id] || 0 })).filter((x) => x.qty > 0),
    [rows, qtyById]
  );
  const selectedPieces = selected.reduce((s, x) => s + x.qty, 0);

  // One print job feeds one roll, so a mixed 45x60 + 50x80 selection opens one
  // window per stock and tells the operator to change the roll between them.
  const printSelected = () => {
    if (selected.length === 0) return;
    const groups = groupCnLabelsBySize(selected.map((x) => toLabelData(x.row, x.qty)));
    groups.forEach(([size, labels]) => {
      const pieces = labels.reduce((s, l) => s + (l.copies || 1), 0);
      const layout = cnSheetLayout(size, sheet);
      // Say how many PIECES of stock this consumes, not just how many stickers —
      // on the big stock the operator has to load them one at a time.
      const detail = layout
        ? `${pieces} 張 → ${Math.ceil(pieces / layout.perSheet)} 大張（每張 ${layout.perSheet} 枚）`
        : `${pieces} 張`;
      enqueueSnackbar(`${CN_LABEL_SIZES[size].label}：${detail}`, { variant: 'info' });
      printNokeCnLabels(labels, sheet);
    });
    if (groups.length > 1) {
      enqueueSnackbar('選取包含兩種尺寸，已分成兩個列印視窗 — 請換捲紙後再印第二批', {
        variant: 'warning',
        autoHideDuration: 8000,
      });
    }
  };

  const printOne = (row: CnLabelRow) => {
    // eslint-disable-next-line no-alert
    const input = window.prompt(`列印 ${row.sku} 中文標籤，數量：`, '1');
    if (input == null) return;
    const copies = parseInt(input, 10);
    if (!copies || copies <= 0) {
      enqueueSnackbar('請輸入有效的列印數量', { variant: 'warning' });
      return;
    }
    printNokeCnLabels([toLabelData(row, copies)], sheet);
  };

  const remove = async (row: CnLabelRow) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`刪除 ${row.sku} 的中文標籤？`)) return;
    try {
      await api.delete(`/api/noke/cn-labels/${row.id}`);
      enqueueSnackbar('已刪除', { variant: 'success' });
      fetchRows();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || '刪除失敗', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ mr: 1 }}>
            中文標籤
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            進口化粧品貼在商品上的法規標示。標準 {CN_LABEL_SIZES['45x60'].label}；
            End Stick BB 用 {CN_LABEL_SIZES['50x80'].label}。
            {sheet !== 'roll' && (
              <>
                {' '}
                目前用大張紙：45×60 一張印 {cnSheetLayout('45x60', sheet)?.perSheet} 枚、
                50×80 一張印 {cnSheetLayout('50x80', sheet)?.perSheet} 枚，印完沿灰線裁開。
              </>
            )}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>紙張</InputLabel>
            <Select
              label="紙張"
              value={sheet}
              onChange={(e) => chooseSheet(e.target.value as CnSheetMode)}
            >
              {(Object.keys(CN_SHEET_MODES) as CnSheetMode[]).map((m) => (
                <MenuItem key={m} value={m}>
                  {CN_SHEET_MODES[m].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label="顯示已停用"
          />
          <CnCalibrationButton />
          {isAdmin && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditing(null);
                setEditorOpen(true);
              }}
            >
              新增中文標籤
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            disabled={selectedPieces === 0}
            onClick={printSelected}
          >
            列印選取 ({selectedPieces} 張)
          </Button>
        </Box>
      </Paper>

      <CnLabelEditor
        open={editorOpen}
        editing={editing}
        defaults={defaults}
        onClose={() => setEditorOpen(false)}
        onSaved={fetchRows}
      />

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            尚未建立任何中文標籤{isAdmin ? ' — 按「新增中文標籤」開始' : ''}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>品名 / 款式</TableCell>
                  <TableCell>淨含量</TableCell>
                  <TableCell>原廠條碼</TableCell>
                  <TableCell>尺寸</TableCell>
                  <TableCell>完整度</TableCell>
                  <TableCell align="center">列印張數</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const qty = qtyById[row.id] || 0;
                  return (
                    <TableRow key={row.id} sx={{ opacity: row.is_active ? 1 : 0.55 }}>
                      <TableCell>
                        <code>{row.sku}</code>
                      </TableCell>
                      <TableCell>
                        {row.product_name}
                        {row.variant_name ? (
                          <Chip size="small" label={row.variant_name} sx={{ ml: 0.5, height: 18 }} />
                        ) : null}
                        {!row.is_active && (
                          <Chip size="small" color="default" label="停用" sx={{ ml: 0.5, height: 18 }} />
                        )}
                      </TableCell>
                      <TableCell>{row.net_content || '—'}</TableCell>
                      <TableCell>
                        {row.barcode ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <code style={{ fontSize: 12 }}>{row.barcode}</code>
                            {/* Only flag a 13-digit code that fails its check digit —
                                a non-EAN symbology is not an error. */}
                            {row.barcode_valid === false && (
                              <Tooltip title="EAN-13 檢查碼不符，請確認條碼是否輸入錯誤">
                                <Chip size="small" color="error" label="?" sx={{ height: 18 }} />
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{CN_LABEL_SIZES[row.label_size]?.label || row.label_size}</TableCell>
                      <TableCell>
                        {row.gaps && row.gaps.length > 0 ? (
                          <Tooltip title={`缺少：${row.gaps.join('、')}`}>
                            <Chip size="small" color="warning" label={`缺 ${row.gaps.length} 項`} />
                          </Tooltip>
                        ) : (
                          <Chip size="small" color="success" label="完整" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          <IconButton size="small" onClick={() => setQty(row.id, qty - 1)} disabled={qty <= 0}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={qty}
                            onChange={(e) => setQty(row.id, parseInt(e.target.value, 10) || 0)}
                            inputProps={{ min: 0, style: { textAlign: 'center', width: 44 } }}
                          />
                          <IconButton size="small" onClick={() => setQty(row.id, qty + 1)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          title="預覽"
                          onClick={() => previewNokeCnLabels([toLabelData(row, 1)])}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" title="列印" onClick={() => printOne(row)}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                        {isAdmin && (
                          <>
                            <IconButton
                              size="small"
                              title="編輯"
                              onClick={() => {
                                setEditing(row);
                                setEditorOpen(true);
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" title="刪除" onClick={() => remove(row)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

// =============================================================================
// Page shell
// =============================================================================

const NokeCounterPage: React.FC = () => {
  const [tab, setTab] = useState(1);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        NOKE 專櫃管理
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab value={1} label="進貨 / 標籤" />
          <Tab value={2} label="中文標籤" />
          <Tab value={3} label="庫存" />
          <Tab value={4} label="銷售紀錄" />
          <Tab value={5} label="日結" />
          <Tab value={0} label="補貨" />
          <Tab value={6} label="補貨 v2" />
        </Tabs>
      </Paper>

      {tab === 0 && <RestockTab />}
      {tab === 6 && <RestockV2Tab />}
      {tab === 1 && <StockInTab />}
      {tab === 2 && <CnLabelTab />}
      {tab === 3 && <InventoryTab />}
      {tab === 4 && <SalesTab />}
      {tab === 5 && <DailyTab />}
    </Box>
  );
};

export default NokeCounterPage;
